import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import DropDownMultiple from "@components/ui/dropdown/DropDownMultiple";
import { useState } from "react";
import { emailTemplatesApi } from "@services/api";
import TextField from "@components/ui/textfields/TextField";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { showNotification } from "context/notification.service";
import { EmailTemplate } from "types/email_template";
import { Timestamp } from "firebase/firestore";

export const EmailTemplateModal = ({
  onClose,
  templateId,
}: {
  onClose: () => void;
  templateId: string;
}) => {
  const emailTemplates = useProjectStore((state) => state.emailTemplates);
  const updateEmailTemplate = useProjectStore(
    (state) => state.updateEmailTemplate,
  );
  const removeEmailTemplates = useProjectStore(
    (state) => state.removeEmailTemplates,
  );
  const libraryDocuments = useProjectStore((state) => state.libraryDocuments);
  const userData = useDataStore((state) => state.userData);

  const isExisting = !!templateId;
  const blankTemplate: EmailTemplate = {
    id: "",
    name: "",
    destination_type: "client",
    subject: "",
    body: "",
    library_documents_id: [],
    created_at: Timestamp.now(),
  };
  const [template, setTemplate] = useState<EmailTemplate>(
    emailTemplates.find((t) => t.id === templateId) ?? blankTemplate,
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleSave = async () => {
    if (!template.name.trim()) {
      showNotification({ type: "error", message: "Le nom est obligatoire" });
      return;
    }
    if (isExisting) {
      updateEmailTemplate(template);
      showNotification({ type: "success", message: "Modèle enregistré" });
      onClose();
    } else {
      try {
        const result = await emailTemplatesApi.create({
          ...template,
          org_id: userData?.org_id || "",
        } as any);
        useProjectStore.getState().setEmailTemplates([
          ...useProjectStore.getState().emailTemplates,
          result,
        ]);
        showNotification({ type: "success", message: "Modèle créé avec succès" });
        onClose();
      } catch {
        showNotification({ type: "error", message: "Erreur lors de la création du modèle" });
      }
    }
  };

  const handleDelete = () => {
    if (!template) return;
    removeEmailTemplates([template.id]);
    showNotification({
      type: "success",
      message: "Modèle supprimé",
    });
    onClose();
  };

  return (
    <>
    {showDeleteConfirm && (
      <ConfirmationModal
        title="Supprimer ce modèle d'email ?"
        message={`Le modèle "${template?.name}" sera supprimé définitivement.`}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
      />
    )}
    <BaseModal
      title="Modèle d'email"
      startIcon={<Icon1 name="email" size={20}></Icon1>}
      onClose={onClose}
    >
      <div className="bg-white flex flex-col gap-2 p-4">
        <TextField
          label="Nom"
          placeholder="Modèle pour pièces complémentaires"
          value={template?.name}
          onChange={(value) =>
            setTemplate((prev) =>
              prev ? { ...prev, name: value } : prev,
            )
          }
        />
        <AreaTextField
          rows={1}
          label="Sujet"
          placeholder="Demande de pièces complémentaires"
          value={template?.subject}
          onChange={(value) =>
            setTemplate((prev) =>
              prev ? { ...prev, subject: value } : prev,
            )
          }
        />
        <AreaTextField
          rows={12}
          label="Contenu"
          placeholder={`Bonjour,

Pouvez-vous nous transmettre l'étude thermique, les plans  ainsi que l'accord de permis de construire du dossier @nom du mandataire @client sur la commune de @ville.

En vous remerciant par avance.

Cordialement,

* Ne pas répondre à ce mail
`}
          value={template?.body}
          onChange={(value) =>
            setTemplate((prev) =>
              prev ? { ...prev, body: value } : prev,
            )
          }
        />
        <div className="p-2 text-h3 text-neutral-400 ">
          @contacts @nom_projet @adresse_projet @signature
        </div>

        <DropDownMultiple
          label="Documents de la bibliothèque"
          placeholder="Sélectionner des documents"
          options={libraryDocuments.map((doc) => ({
            value: doc.id,
            label: doc.name,
          }))}
          value={template?.library_documents_id || []}
          onSelect={(ids) =>
            setTemplate((prev) =>
              prev
                ? { ...prev, library_documents_id: ids as string[] }
                : prev,
            )
          }
        />
      </div>

      <div className="border-t-1 p-4 flex flex-row items-center gap-2">
        {isExisting && (
          <SecondaryButton
            variant="small"
            danger
            onClick={() => setShowDeleteConfirm(true)}
          >
            Supprimer
          </SecondaryButton>
        )}
        <div className="ml-auto flex flex-row gap-2">
          <SecondaryButton variant="small" onClick={onClose}>
            Annuler
          </SecondaryButton>
          <Button variant="small" label="Enregistrer" onClick={handleSave} />
        </div>
      </div>
    </BaseModal>
    </>
  );
};
